import { useSnackbar, Document } from '@ecdlink/core';
import { FileTypeEnum, WorkflowStatusEnum } from '@ecdlink/graphql';
import {
  Alert,
  BannerWrapper,
  Button,
  classNames,
  DatePicker,
  Dialog,
  DialogPosition,
  Dropdown,
  IMAGE_WIDTH,
  ImageInput,
  Typography,
  renderIcon,
} from '@ecdlink/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PhotoPrompt } from '../../../components/photo-prompt/photo-prompt';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import { useAppDispatch } from '@store';
import { userSelectors } from '@store/user';
import { analyticsActions } from '@store/analytics';
import {
  UploadDocumentModel,
  UploadDocumentModelSchema,
} from '@/schemas/practitioner/upload-tocument';
import { documentActions, documentThunkActions } from '@/store/document';
import { authSelectors } from '@/store/auth';
import { useStaticData } from '@/hooks/useStaticData';
import { newGuid } from '@/utils/common/uuid.utils';
import ROUTES from '@routes/routes';
import { getMonthsList, toUtcMidnightDate } from '@/utils/common/date.utils';
import { getFileTypeFromBase64 } from '@/utils/common/base64StringCleanUp.utils';

// Map document type string to FileTypeEnum
const fileTypeEnumMap: Record<string, FileTypeEnum> = {
  IdentityDocument: FileTypeEnum.IdentityDocument,
  ProofOfAccount: FileTypeEnum.ProofOfAccount,
  PractitionerAgreement: FileTypeEnum.PractitionerAgreement,
  StartupSupportAgreement: FileTypeEnum.StartupSupportAgreement,
  ProofOfSiteAddress: FileTypeEnum.ProofOfSiteAddress,
  WeeklyAttendanceRegister: FileTypeEnum.WeeklyAttendanceRegister,
  MonthlyIncomeExpensesStatement: FileTypeEnum.MonthlyIncomeExpensesStatement,
};

const documentTypes = [
  { label: 'Identity document', value: 'IdentityDocument' },
  { label: 'Proof of account', value: 'ProofOfAccount' },
  { label: 'Franchisee agreement', value: 'PractitionerAgreement' },
  { label: 'Start-up agreement', value: 'StartupSupportAgreement' },
  { label: 'Lease agreement / Affidavit', value: 'ProofOfSiteAddress' },
  { label: 'Attendance Register', value: 'WeeklyAttendanceRegister' },
  {
    label: 'Monthly Income and Expenses Statement',
    value: 'MonthlyIncomeExpensesStatement',
  },
];

export const PractitionerUploadDocument: React.FC = () => {
  const { getWorkflowStatusIdByEnum, getDocumentTypeIdByEnum } =
    useStaticData();
  const history = useHistory();
  const appDispatch = useAppDispatch();
  const { isOnline } = useOnlineStatus();
  const user = useSelector(userSelectors.getUser);
  const userAuth = useSelector(authSelectors.getAuthUser);
  const { showMessage } = useSnackbar();

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [startOfWeek, setStartOfWeek] = useState<Date | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string | undefined>();
  const [photoActionBarVisible, setPhotoActionBarVisible] =
    useState<boolean>(false);
  const [documentUrl, setDocumentUrl] = useState<string>();
  const [documentType, setDocumentType] = useState<string>('');
  const acceptedFormats = ['jpg', 'png', 'pdf'];

  const {
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<UploadDocumentModel>({
    resolver: yupResolver(UploadDocumentModelSchema),
    mode: 'onChange',
    defaultValues: {
      documentType: '',
      documentUpload: '',
      documentDate: null,
      startOfWeek: null,
    },
  });

  const today = new Date();
  const minDate = new Date(today.getFullYear(), today.getMonth() - 11, 1);

  const effectiveDocType = documentType;
  const isWeeklyAttendanceRegister =
    effectiveDocType === 'WeeklyAttendanceRegister';
  const isMonthlyIncomeExpenses =
    effectiveDocType === 'MonthlyIncomeExpensesStatement';

  function handleStartOfWeekChange(date: Date) {
    if (date.getDay() === 1) {
      setStartOfWeek(date);
      setValue('startOfWeek', date, { shouldValidate: true });
      setValue('documentDate', date, { shouldValidate: true });
      return;
    }

    setStartOfWeek(null);
    setValue('startOfWeek', null, { shouldValidate: true });
    setValue('documentDate', null, { shouldValidate: true });
  }

  useEffect(() => {
    setSelectedMonth(undefined);
    setSelectedDate(null);
    setStartOfWeek(null);
    setValue('documentDate', null, { shouldValidate: true });
    setValue('startOfWeek', null, { shouldValidate: true });
  }, [documentType, setValue]);

  useEffect(() => {
    if (!isWeeklyAttendanceRegister && !isMonthlyIncomeExpenses) {
      setValue('documentDate', selectedDate, { shouldValidate: true });
    }
  }, [
    selectedDate,
    isWeeklyAttendanceRegister,
    isMonthlyIncomeExpenses,
    setValue,
  ]);

  useEffect(() => {
    if (isMonthlyIncomeExpenses) {
      setValue('documentDate', selectedMonth ? new Date(selectedMonth) : null, {
        shouldValidate: true,
      });
    }
  }, [selectedMonth, isMonthlyIncomeExpenses, setValue]);

  useEffect(() => {
    if (!isOnline) {
      appDispatch(
        analyticsActions.createViewTracking({
          pageView: window.location.pathname,
          title: 'Upload Documents',
        })
      );
    }
  }, [isOnline, appDispatch]);

  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (data: UploadDocumentModel) => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const selectedDocType = documentType || data.documentType;
      const fileType = fileTypeEnumMap[selectedDocType];

      if (!fileType) return;
      if (!documentUrl) return;
      if (!user?.id || !userAuth?.auth_token) return;

      const workflowStatusId = getWorkflowStatusIdByEnum(
        WorkflowStatusEnum.DocumentVerified
      );
      const documentTypeId = getDocumentTypeIdByEnum(fileType);
      if (!workflowStatusId || !documentTypeId) return;

      const fileName =
        `${selectedDocType}_${user?.id ?? ''}_` +
        getFileTypeFromBase64(documentUrl);

      let rawDate: Date | null = null;
      if (selectedDocType === 'WeeklyAttendanceRegister') {
        if (!startOfWeek) return;
        rawDate = startOfWeek;
      } else if (selectedDocType === 'MonthlyIncomeExpensesStatement') {
        if (!selectedMonth) return;
        rawDate = new Date(selectedMonth);
      } else {
        if (!selectedDate) return;
        rawDate = selectedDate;
      }

      const utcDate = toUtcMidnightDate(rawDate);

      const documentInput: Document = {
        id: newGuid(),
        documentTypeId,
        workflowStatusId,
        createdUserId: user?.id ?? '',
        name: fileName,
        documentDate: utcDate,
        userId: user?.id ?? '',
        fileName,
        file: documentUrl,
        fileType,
      };

      appDispatch(documentActions.createDocument(documentInput));
      if (isOnline) {
        try {
          await appDispatch(
            documentThunkActions.createDocument({ document: documentInput })
          ).unwrap();
        } catch {
          return;
        }
      }
      // success feedback
      showMessage({
        type: 'success',
        message: 'Document uploaded successfully',
      });

      history.push(ROUTES.PRACTITIONER.PROFILE.ROOT);
    } finally {
      setSubmitting(false);
    }
  };

  const setPhotoUrl = (imageUrl: string) => {
    setDocumentUrl(imageUrl);
    setValue('documentUpload', imageUrl, { shouldValidate: true });
    setPhotoActionBarVisible(false);
  };

  return (
    <>
      <form
        className="flex min-h-screen flex-col bg-white"
        onSubmit={handleSubmit(onSubmit)}
      >
        <BannerWrapper
          showBackground={false}
          size="medium"
          renderBorder
          title="Upload a Document"
          color="primary"
          onBack={history.goBack}
          displayOffline={!isOnline}
        />

        <div className="flex justify-center">
          <div className="w-11/12 rounded-2xl">
            <Alert
              className="mt-2 rounded-md"
              message="Please note:"
              list={[
                'Make sure the uploaded image of your document is readable (not blurry)',
                'If you are uploading an ID, Bank statement, or an affidavit, make sure that the copy is certified',
                'If you have more than one class assigned to you, please combine weekly registers into one upload per week.',
              ]}
              type="info"
            />
          </div>
        </div>

        <div className="mx-auto w-11/12 flex-1 py-2 pb-20">
          <Typography text="Document Type:" type="h4" color="textMid" />
          <Dropdown
            placeholder="Document Type"
            list={documentTypes}
            fillType="clear"
            fullWidth
            className={classNames('w-full')}
            selectedValue={documentType}
            onChange={(value: string) => {
              setValue('documentType', value, { shouldValidate: true });
              setDocumentType(value);
            }}
          />

          {isMonthlyIncomeExpenses && (
            <div className="mt-4 w-60">
              <Typography
                text="Which month are you submitting for?"
                type="h4"
                color="textMid"
              />
              <Dropdown
                placeholder="Select month"
                list={getMonthsList()}
                fullWidth
                className={classNames('w-full')}
                selectedValue={selectedMonth}
                onChange={(monthValue: string) => setSelectedMonth(monthValue)}
              />
            </div>
          )}

          {isWeeklyAttendanceRegister && !isMonthlyIncomeExpenses && (
            <div className="mt-4 flex w-60 flex-col gap-1">
              <Typography
                text="Select start date of the week:"
                type="h4"
                color="textMid"
              />
              <div className="relative">
                <DatePicker
                  placeholderText="Select start date of the week"
                  value={startOfWeek ? startOfWeek.toDateString() : ''}
                  className="focus:border-primary focus:ring-primary w-full rounded-md pr-10 text-sm text-black shadow-sm"
                  selected={startOfWeek ? new Date(startOfWeek) : null}
                  onChange={handleStartOfWeekChange}
                  dateFormat="EEE, dd MMM yyyy"
                  showYearDropdown
                  scrollableYearDropdown
                  maxDate={today}
                  minDate={minDate}
                  filterDate={(date: Date) => date.getDay() === 1}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 transform">
                  {renderIcon('CalendarIcon', 'h-5 w-5 text-gray-400')}
                </span>
              </div>
            </div>
          )}

          {!isWeeklyAttendanceRegister && !isMonthlyIncomeExpenses && (
            <div className="mt-4 flex w-60 flex-col gap-1">
              <Typography text="Date:" type="h4" color="textMid" />
              <div className="relative">
                <DatePicker
                  placeholderText="Please select a date"
                  value={selectedDate ? selectedDate.toDateString() : ''}
                  className="focus:border-primary focus:ring-primary w-full rounded-md pr-10 text-sm text-black shadow-sm"
                  selected={selectedDate ? new Date(selectedDate) : null}
                  onChange={(date: Date | null) => setSelectedDate(date)}
                  dateFormat="EEE, dd MMM yyyy"
                  showYearDropdown
                  scrollableYearDropdown
                  maxDate={today}
                  minDate={minDate}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 transform">
                  {renderIcon('CalendarIcon', 'h-5 w-5 text-gray-400')}
                </span>
              </div>
            </div>
          )}

          <div className="mt-4">
            <Typography
              text="Please upload a document"
              type="h4"
              color="textMid"
            />
            <div style={{ position: 'relative' }}>
              <ImageInput<UploadDocumentModel>
                nameProp={'documentUpload'}
                acceptedFormats={acceptedFormats}
                resolutionLimit={IMAGE_WIDTH}
                icon="CameraIcon"
                label=""
                className="w-100"
                currentImageString={documentUrl}
                overrideOnClick={() => setPhotoActionBarVisible(true)}
                onValueChange={(url: string) => {
                  setDocumentUrl(url);
                  setValue('documentUpload', url, { shouldValidate: true });
                }}
              />
            </div>
          </div>

          <div className="mt-4 mb-8">
            <Button
              type="filled"
              className="w-full"
              size="small"
              color="primary"
              disabled={
                !isValid ||
                !isOnline ||
                (isMonthlyIncomeExpenses && !selectedMonth) ||
                (isWeeklyAttendanceRegister &&
                  !isMonthlyIncomeExpenses &&
                  !startOfWeek) ||
                (!isWeeklyAttendanceRegister &&
                  !isMonthlyIncomeExpenses &&
                  !selectedDate) ||
                !documentUrl
              }
              onClick={handleSubmit(onSubmit)}
            >
              {renderIcon('ArrowCircleRightIcon', 'h-5 w-5 text-white')}
              <Typography
                type="h6"
                className="ml-2"
                text="Submit"
                color="white"
              />
            </Button>
          </div>
        </div>

        <Dialog
          visible={photoActionBarVisible}
          position={DialogPosition.Bottom}
          stretch
        >
          <PhotoPrompt
            title="Child registration form"
            resolutionLimit={IMAGE_WIDTH}
            onClose={() => setPhotoActionBarVisible(false)}
            onAction={(imageUrl: string) => {
              setPhotoUrl(imageUrl);
              setValue('documentUpload', imageUrl, { shouldValidate: true });
            }}
            onDelete={() => {
              setDocumentUrl(undefined);
              setValue('documentUpload', '', { shouldValidate: true });
              setPhotoActionBarVisible(false);
            }}
          />
        </Dialog>
      </form>
    </>
  );
};
